"use client"

import type {
  BankTransferInfo,
  Booking,
  BookingCharge,
  CourtCategory,
  HourCategory,
  HourPackage,
  Member,
  Purchase,
  PurchasePaymentMethod,
} from "@/lib/types"
import type { CourtCategoryMeta } from "./courts"
import type { MembershipBookingResource } from "./membership-bookings"
import { fromApiGender } from "./profile"
import type {
  BankTransferResource,
  BookingResource,
  CourtResource,
  MembershipResource,
  PackagePurchaseResource,
  PackageResource,
  PaymentMethodResource,
  UserResource,
} from "./types"

/**
 * Map the API `UserResource` to the app's `Member`.
 *
 * Card number (`human_code`) and member type now come from the backend. Join
 * date, expiry, and hour balances belong to /memberships and are overlaid via
 * mergeMembership — the neutral defaults here show only while that query is in
 * flight.
 */
export function toMember(u: UserResource): Member {
  return {
    id: u.id,
    fullName: u.name ?? "",
    username: u.username ?? "",
    phone: u.phone ?? "",
    email: u.email ?? "",
    // Schema marks these non-nullable but the API returns null when unverified;
    // treat as optional and normalise to null so the hydration guard can rely
    // on a single check.
    emailVerifiedAt: u.email_verified_at ?? null,
    phoneVerifiedAt: u.phone_verified_at ?? null,
    dob: u.date_of_birth ?? "",
    gender: fromApiGender(u.gender),
    photoUrl: u.profile_picture_url ?? undefined,
    cardNumber: u.human_code ?? "",
    memberType:
      u.membership_member_type?.toLowerCase() === "corporate"
        ? "corporate"
        : "personal",
    membershipName: "",
    status: null,
    joinedAt: "",
    expiresAt: "",
    peak: { used: 0, total: 0 },
    regular: { used: 0, total: 0 },
  }
}

/**
 * Overlay club-membership data from `/memberships` onto an identity Member.
 *
 * G2: the API exposes single `hours_peak`/`hours_off_peak` integers, which are
 * the *remaining* balance (backend `Membership::syncHourTotals` sums the ledger).
 * There's no granted/total, so the home card shows remaining-only — we map the
 * value to `total` with `used: 0` purely so `total - used` yields remaining.
 * Card number + member type stay client-derived (G1, G3); see backend-gaps.md.
 */
export function mergeMembership(member: Member, m: MembershipResource): Member {
  return {
    ...member,
    membershipName: m.name,
    status: m.status,
    joinedAt: m.joined_at.slice(0, 10),
    expiresAt: m.expires_at.slice(0, 10),
    peak: { used: 0, total: m.hours_peak },
    regular: { used: 0, total: m.hours_off_peak },
  }
}

/**
 * Map a BookingResource to the app's Booking.
 *
 * The played hours are derived from `booked_at` (start hour) + `duration_hours`
 * — the client sets booked_at to the start hour on create, and the server
 * echoes it back. Court name/tier are resolved from `courtsById` (the booking
 * items only carry court_id).
 */
export function toBooking(
  b: BookingResource,
  courtsById: Map<string, CourtResource>
): Booking {
  const startHour = Number(b.booked_at.match(/T(\d{2}):/)?.[1] ?? 0)
  const hours = Array.from(
    { length: b.duration_hours },
    (_, i) => startHour + i
  )
  // Peak/off-peak is backend-owned: each item carries a snapshot `is_peak`
  // (frozen at booking time, honours special schedules). Filter nulls so
  // legacy pre-snapshot rows don't skew the ratio — they fall back to
  // "regular". `hours` above is still used to render the schedule.
  const peaks = (b.items ?? [])
    .map((it) => it.is_peak)
    .filter((p): p is boolean => p != null)
  const peakCount = peaks.filter(Boolean).length
  const total = peaks.length
  const category: HourCategory =
    total > 0 && peakCount >= total / 2 ? "peak" : "regular"
  const mixed = peakCount > 0 && peakCount < total

  const court = courtsById.get(b.items?.[0]?.court_id ?? "")

  return {
    id: b.id,
    ref: b.human_booking_id,
    categoryId: court?.court_category?.id ?? "",
    categoryName: court?.court_category?.name ?? "",
    courtLabel: court?.name ?? "Court",
    date: b.booked_at.slice(0, 10),
    hours,
    category,
    mixed,
    peakHours: peakCount,
    regularHours: total - peakCount,
    durationHours: b.duration_hours,
    charge: toCharge(b.order),
    payment: b.payment_method,
    paymentUrl: b.payment_url ?? null,
    paymentExpiresAt: b.payment_expires_at ?? null,
    createdAt: b.created_at ?? "",
    status: b.status as Booking["status"],

    // Deliberately NOT defaulted: `undefined` means the backend never sent a
    // verdict, and lib/booking.ts falls back to a local rule only in that case.
    // Coercing to `false`/`null` here would silently disable both actions
    // against an older backend.
    changeDeadlineAt: b.change_deadline_at,
    canReschedule: b.can_reschedule,
    canCancel: b.can_cancel,
    changeBlockedReason: b.change_blocked_reason,
    cancelBlockedReason: b.cancel_blocked_reason,

    // `off_peak` → `regular` follows the naming this mapper already uses for
    // regularHours above; the app's HourCategory vocabulary has no "off peak".
    refundableHours: {
      peak: b.hours_refundable_peak ?? 0,
      regular: b.hours_refundable_off_peak ?? 0,
    },

    cancelledAt: b.cancelled_at ?? null,
    cancelledReason: b.cancelled_reason ?? null,
    cancelledBy: b.cancelled_by ?? null,
    scheduledEndAt: b.scheduled_end_at ?? null,
    expiredAt: b.expired_at ?? null,
  }
}

/**
 * Map the booking's order to what the customer owes.
 *
 * The backend sends `total_amount` as the subtotal *before* deductions and
 * `total_adjustment` as their sum, so the payable total is the difference —
 * never render total_amount on its own or a discounted booking overstates the
 * bill. Money arrives as decimal strings ("350000.00") to dodge float drift in
 * transit; Number() is safe here since IDR sums stay far inside 2^53.
 */
function toCharge(order: BookingResource["order"]): BookingCharge | null {
  if (!order) return null

  const subtotalIdr = Number(order.total_amount)
  // Backend stores adjustments signed (discount is negative). Negate to the
  // positive magnitude that types.ts promises, so the subtract below and the
  // "-" prefix in the detail view read correctly. ponytail: assumes discounts
  // only; flip if backend ever sends positive surcharges.
  const discountIdr = -Number(order.total_adjustment)

  return {
    orderRef: order.human_order_id,
    subtotalIdr,
    discountIdr,
    totalIdr: subtotalIdr - discountIdr,
    adjustments: (order.adjustments ?? []).map((a) => ({
      label: a.description ?? "",
      amountIdr: -Number(a.amount),
    })),
  }
}

/**
 * Map a PackageResource to the app's HourPackage.
 *
 * Money arrives as decimal strings ("350000.00"); Number() is safe for IDR sums
 * (well inside 2^53). `fee_amount`/`total_price` only come back when the package
 * was queried with a payment_method_id, so they map to optional fields.
 */
export function toPackage(p: PackageResource): HourPackage {
  return {
    id: p.id,
    name: p.name,
    description: p.description,
    peakHours: p.hours_peak,
    offPeakHours: p.hours_off_peak,
    durationDays: p.duration_days ?? 0,
    priceIdr: Number(p.price),
    feeIdr: p.fee_amount != null ? Number(p.fee_amount) : undefined,
    totalIdr: p.total_price != null ? Number(p.total_price) : undefined,
  }
}

/** Map a PaymentMethodResource to the app's PurchasePaymentMethod. */
export function toPaymentMethod(
  m: PaymentMethodResource
): PurchasePaymentMethod {
  return {
    id: m.id,
    paymentType: m.payment_type,
    category: m.category,
    name: m.name,
    feeType: m.fee_type,
    feeValue: Number(m.fee_value),
    feeFixed: Number(m.fee_fixed),
    description: m.description,
    imageUrl: m.image ?? undefined,
    status: m.status,
  }
}

/** Map a BankTransferResource to the app's BankTransferInfo. */
export function toBankTransfer(b: BankTransferResource): BankTransferInfo {
  return {
    isAvailable: b.is_available,
    bankName: b.bank_name,
    accountNumber: b.account_number,
    accountHolder: b.account_holder,
    branch: b.branch ?? undefined,
    instructions: b.instructions ?? undefined,
  }
}

/**
 * Map a PackagePurchaseResource to the app's Purchase.
 *
 * Money arrives as decimal strings; Number() is safe (see toCharge). Adjustments
 * are kept signed (`amountIdr = Number(amount)`) — unlike toCharge's discount
 * flip — since the backend already signs fees/discounts here. `payment_method`
 * and `bank_transfer` are null-safe; `proofUploaded` is derived from the
 * presence of a payment_proof_url.
 */
export function toPurchase(p: PackagePurchaseResource): Purchase {
  return {
    id: p.id,
    orderRef: p.human_order_id,
    status: p.status,
    package: toPackage(p.package),
    method: p.payment_method ? toPaymentMethod(p.payment_method) : null,
    priceIdr: Number(p.price),
    feeIdr: Number(p.fee),
    totalIdr: Number(p.total_charged),
    adjustments: (p.adjustments ?? []).map((a) => ({
      id: a.id,
      type: a.type,
      description: a.description ?? undefined,
      amountIdr: Number(a.amount),
    })),
    paymentUrl: p.payment_url ?? null,
    paymentExpiresAt: p.payment_expires_at ?? null,
    paidAt: p.paid_at ?? null,
    bankTransfer: p.bank_transfer ? toBankTransfer(p.bank_transfer) : null,
    proofUrl: p.payment_proof_url ?? null,
    proofUploaded: !!p.payment_proof_url,
    note: p.payment_note ?? null,
    reviewedAt: p.reviewed_at ?? null,
    rejectedReason: p.rejected_reason ?? null,
    createdAt: p.created_at ?? "",
  }
}

/**
 * A booking from the membership-scoped history, augmented with the user who
 * booked it. Shared (e.g. corporate) memberships pool hours across multiple
 * users, so each row carries the booker's identity for the "You" chip + display.
 */
export interface MembershipBooking extends Booking {
  bookedBy: { id: string; name: string } | null
}

export function toMembershipBooking(
  b: MembershipBookingResource,
  courtsById: Map<string, CourtResource>
): MembershipBooking {
  return {
    ...toBooking(b, courtsById),
    bookedBy: b.user ? { id: b.user.id, name: b.user.name } : null,
  }
}

/** A bookable slot for one hour on one court, mapped from the availability API. */
export interface AvailabilitySlot {
  scheduleId: string
  scheduleType: "regular" | "special"
  hour: number // 24h start hour, from start_time
  isPeak: boolean
  status: "available" | "booked" | "unavailable"
  /**
   * The club event holding this hour — an Americano night, a clinic — so the
   * grid can say what took the court instead of a bare "Unavailable". Null
   * whenever there is nothing to name.
   */
  blockedBy: string | null
  /**
   * Who to ask about that event, when the club named someone. Only ever set
   * alongside `blockedBy` — a contact with no event to belong to would read as
   * a stray name in the middle of the grid.
   */
  blockedByContact: string | null
}

export function toAvailabilitySlot(s: {
  schedule_id: string
  schedule_type: "regular" | "special"
  /** Optional: a response cached by the service worker may predate this field. */
  special_schedule_name?: string | null
  /** Optional for the same reason, and newer than the name beside it. */
  special_schedule_contact_person?: string | null
  start_time: string
  is_peak: boolean
  status: "available" | "booked" | "unavailable"
}): AvailabilitySlot {
  // Set here, from the status the BACKEND sent, and nowhere else. Screens
  // downgrade slots to "unavailable" for their own reasons — reschedule greys
  // out the wrong rate class — and those must keep reading as a plain
  // Unavailable rather than borrowing the name of an event that is not why
  // they are blocked.
  const blockedBy =
    s.status === "unavailable" ? s.special_schedule_name?.trim() || null : null

  return {
    scheduleId: s.schedule_id,
    scheduleType: s.schedule_type,
    hour: Number(s.start_time.slice(0, 2)),
    isPeak: s.is_peak,
    status: s.status,
    blockedBy,
    blockedByContact: blockedBy
      ? s.special_schedule_contact_person?.trim() || null
      : null,
  }
}

/**
 * Group flat backend courts into the app's categories (G4).
 *
 * Category identity is fully backend-owned: each court carries its
 * `court_category` (id + display profile) from /courts, and /court-categories
 * supplies the canonical order + editable name/subtitle/thumbnail. Courts group
 * by `court_category.id`. Price lives on each court, not the category (courts
 * in the same category can be priced differently) — the exact rate is read
 * from the specific court being booked, not stored here. `fromRate` is only
 * the cheapest regular rate among the category's courts, for a "Start at" hint.
 * Categories with no backend courts are dropped.
 */
export function toCourtCategories(
  courts: CourtResource[],
  categories: CourtCategoryMeta[] = []
): CourtCategory[] {
  const byCategoryId = new Map<string, CourtResource[]>()
  for (const court of courts) {
    const id = court.court_category?.id
    if (!id) continue
    const list = byCategoryId.get(id)
    if (list) list.push(court)
    else byCategoryId.set(id, [court])
  }

  // Prefer the canonical /court-categories order; otherwise fall back to the
  // categories embedded on the courts themselves.
  const ordered: string[] = categories.length
    ? [...categories]
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((c) => c.id)
    : [...byCategoryId.keys()]
  const metaById = new Map<string, CourtCategoryMeta>()
  for (const c of categories) metaById.set(c.id, c)

  return ordered.flatMap((id, index) => {
    const categoryCourts = byCategoryId.get(id)
    if (!categoryCourts || categoryCourts.length === 0) return []

    const meta = metaById.get(id)
    const embedded = categoryCourts[0].court_category
    const regularRates = categoryCourts
      .map((c) => c.prices?.[0]?.price_hours_off_peak)
      .filter((r): r is string => r != null)
      .map(Number)
    return [
      {
        id,
        name: meta?.name ?? embedded?.name ?? "",
        subtitle: meta?.subtitle ?? categoryCourts[0].description ?? "",
        photoUrl: meta?.image_url ?? embedded?.image_url ?? "",
        sortOrder: meta?.sort_order ?? embedded?.sort_order ?? index,
        courts: categoryCourts.map((c) => c.name),
        fromRate: regularRates.length ? Math.min(...regularRates) : 0,
      },
    ]
  })
}
